import React from 'react'
import { ColumnFilterModel } from '../../models/common/ColumnFilterModel'

/**
 * Kolon filtresinin türü:
 * - text: yazılan metni içeren kayıtlar (yazmayı bırakınca ya da Enter ile)
 * - select: listeden bir ya da birkaç değer (Uygula ile)
 * - date: tarih aralığı, başlangıç ve bitiş günü dahil
 * - number: en az / en çok
 */
export type ColumnFilterType = 'text' | 'select' | 'date' | 'number'

export interface ColumnFilterOption {
    label: string
    value: string
}

export interface ColumnFilterDefinition {
    type: ColumnFilterType
    /** Seçimli kolonun seçenekleri. İlk açılışta bir kez yüklenir, sonra hatırlanır. */
    loadOptions?: () => Promise<ColumnFilterOption[]>
    /** Seçimli kolonda "(Boş)" seçeneği: alanı doldurulmamış kayıtlar. Varsayılan: var. */
    allowEmpty?: boolean
    /** Seçeneğin listede nasıl görüneceği (ör. renkli durum rozeti). Verilmezse düz yazı. */
    renderOption?: (label: string) => React.ReactNode
}

/** Kolon adı → filtre tanımı. Tanımı olmayan kolonun altı boş kalır (ör. hesaplanan toplamlar). */
export type ColumnFilterDefinitions = Record<string, ColumnFilterDefinition>

/** Seçimli kolonda "(Boş)" seçeneğinin değeri. Backend: FilterMap.EmptyValue. */
export const COLUMN_FILTER_EMPTY_VALUE = '__empty__'
export const COLUMN_FILTER_EMPTY_LABEL = '(Boş)'

/**
 * Türkçe harfleri Latin karşılığına indirip küçültür: "BAŞAKŞEHİR" → "basaksehir".
 * Backend'deki metin filtresiyle (FilterMap.Fold) aynı kural; listede aramada da kullanılır.
 */
export const foldText = (text: string): string =>
    text
        .replace(/[İIı]/g, 'i')
        .replace(/[Şş]/g, 's')
        .replace(/[Ğğ]/g, 'g')
        .replace(/[Üü]/g, 'u')
        .replace(/[Öö]/g, 'o')
        .replace(/[Çç]/g, 'c')
        .replace(/[Ââ]/g, 'a')
        .replace(/[Îî]/g, 'i')
        .replace(/[Ûû]/g, 'u')
        .toLowerCase()

/** Yazmayı bıraktıktan sonra aramanın başlaması için beklenen süre. */
export const COLUMN_FILTER_DEBOUNCE_MS = 400

export const isColumnFilterActive = (filter: ColumnFilterModel | undefined): boolean => {
    if (!filter)
        return false

    return !!(filter.text && filter.text.trim())
        || !!(filter.values && filter.values.length > 0)
        || !!filter.from
        || !!filter.to
}

// yyyy-MM-dd → dd.MM.yyyy
const formatDate = (value: string | undefined) => {
    if (!value)
        return ''

    const parts = value.split('-')

    return parts.length == 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value
}

const formatNumber = (value: string | undefined) => {
    if (!value)
        return ''

    const n = Number(value.replace(',', '.'))

    return isNaN(n) ? value : n.toLocaleString('tr-TR', { maximumFractionDigits: 2 })
}

/** Filtrenin kısa özeti: tetik düğmesinde ve etiketlerde gösterilir. */
export const columnFilterSummary = (type: ColumnFilterType | undefined, filter: ColumnFilterModel | undefined): string => {
    if (!filter || !isColumnFilterActive(filter))
        return ''

    if (type == 'text' || (!type && filter.text))
        return `"${(filter.text ?? '').trim()}"`

    if (type == 'select' || (!type && filter.values)) {
        const labels = filter.valueLabels && filter.valueLabels.length == (filter.values ?? []).length
            ? filter.valueLabels
            : (filter.values ?? [])

        return labels.length == 1 ? labels[0] : `${labels.length} seçili`
    }

    const format = type == 'date' ? formatDate : formatNumber
    const from = format(filter.from)
    const to = format(filter.to)

    if (from && to)
        return from == to ? from : `${from} – ${to}`

    return from ? `≥ ${from}` : `≤ ${to}`
}
